import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import type { KeyScope, Role, Workspace } from '../api/types'
import { WebhooksCard } from '../components/WebhooksCard'
import { Badge, Button, Card, CodeBlock, CopyButton, CopyField, EmptyState, ErrorNote, Field, LoadingBlock, PageHeader, SecretNotice } from '../components/ui'
import { useMe } from '../hooks/useMe'
import { useCurrentWorkspace } from '../hooks/useWorkspace'
import {
  useApiKeys,
  useCancelInvite,
  useCreateApiKey,
  useInvite,
  useInvites,
  useMembers,
  useRemoveMember,
  useRevokeApiKey,
  useSetMemberRole,
  useUpdateWorkspace,
} from '../hooks/useWorkspaceData'
import { timeAgo, timeUntil } from '../utils/format'

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  editor: 'Editor',
  viewer: 'Viewer',
}

const ROLE_HINTS: Record<Role, string> = {
  owner: 'Everything, billing and these settings included',
  editor: 'Upload, edit, make clips and publish',
  viewer: 'Read only',
}

export function WorkspaceSettingsPage() {
  const { workspace } = useCurrentWorkspace()
  if (!workspace) return <Navigate to="/studio" replace />
  return (
    <>
      <PageHeader title="Workspace settings" subtitle={workspace.name} />
      <div className="stack-lg">
        <GeneralCard workspace={workspace} />
        <MembersCard workspace={workspace} />
        {workspace.role !== 'viewer' && <ApiKeysCard key={workspace.id} workspace={workspace} />}
        {workspace.role !== 'viewer' && <WebhooksCard workspaceId={workspace.id} />}
      </div>
    </>
  )
}

function GeneralCard({ workspace }: { workspace: Workspace }) {
  const [name, setName] = useState(workspace.name)
  const [slug, setSlug] = useState(workspace.slug)
  useEffect(() => {
    setName(workspace.name)
    setSlug(workspace.slug)
  }, [workspace.id, workspace.name, workspace.slug])
  const update = useUpdateWorkspace(workspace.id)
  const owner = workspace.role === 'owner'
  const changed = name.trim() !== workspace.name || slug.trim() !== workspace.slug
  return (
    <Card title="General">
      <form
        className="stack narrow"
        onSubmit={(event) => {
          event.preventDefault()
          update.mutate({ name: name.trim(), slug: slug.trim() })
        }}
      >
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} disabled={!owner} required />
        </Field>
        <Field label="Slug" hint="Used in your public show links. Lowercase letters, numbers and hyphens.">
          <input value={slug} onChange={(e) => setSlug(e.target.value)} maxLength={48} disabled={!owner} required />
        </Field>
        {update.error && <ErrorNote error={update.error} />}
        {owner ? (
          <div className="row">
            <Button type="submit" busy={update.isPending} disabled={!changed}>Save changes</Button>
            {update.isSuccess && !changed && <span className="muted small">Saved.</span>}
          </div>
        ) : (
          <p className="muted small">Only the owner can change these.</p>
        )}
      </form>
    </Card>
  )
}

/** `explain` spells out what each role may do; the members table has no room. */
function RolePicker({ value, onChange, disabled, explain }: { value: Role; onChange: (role: Role) => void; disabled?: boolean; explain?: boolean }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as Role)} disabled={disabled}>
      {(Object.keys(ROLE_LABELS) as Role[]).map((role) => (
        <option key={role} value={role}>
          {explain ? `${ROLE_LABELS[role]} — ${ROLE_HINTS[role]}` : ROLE_LABELS[role]}
        </option>
      ))}
    </select>
  )
}

function MembersCard({ workspace }: { workspace: Workspace }) {
  const workspaceId = workspace.id
  const owner = workspace.role === 'owner'
  const me = useMe()
  const navigate = useNavigate()
  const members = useMembers(workspaceId)
  const invites = useInvites(workspaceId, owner)
  const invite = useInvite(workspaceId)
  const cancel = useCancelInvite(workspaceId)
  const setRole = useSetMemberRole(workspaceId)
  const remove = useRemoveMember(workspaceId)
  const [email, setEmail] = useState('')
  const [role, setRole_] = useState<Role>('editor')
  const [link, setLink] = useState<string | null>(null)
  const myId = me.data?.user?.id
  const owners = members.data?.filter((member) => member.role === 'owner').length ?? 0

  return (
    <Card title="Members">
      <div className="stack">
        {owner && (
          <form
            className="row align-end"
            onSubmit={(event) => {
              event.preventDefault()
              invite.mutate(
                { email: email.trim(), role },
                {
                  onSuccess: (sent) => {
                    setEmail('')
                    setLink(sent.dev_link)
                  },
                },
              )
            }}
          >
            <Field label="Invite by email">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="them@example.com" required />
            </Field>
            <Field label="They can">
              <RolePicker value={role} onChange={setRole_} explain />
            </Field>
            <Button type="submit" busy={invite.isPending} disabled={!email.trim()}>Send invitation</Button>
          </form>
        )}
        {link && <SecretNotice secret={link} message="No email provider is set up, so here is the invitation link to pass on yourself." onDone={() => setLink(null)} />}
        {invite.error && <ErrorNote error={invite.error} />}
        {cancel.error && <ErrorNote error={cancel.error} />}
        {setRole.error && <ErrorNote error={setRole.error} />}
        {remove.error && <ErrorNote error={remove.error} />}

        {members.isPending && <LoadingBlock />}
        {members.isError && <ErrorNote error={members.error} />}
        {members.data && (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Member</th><th>Role</th><th>Joined</th><th /></tr></thead>
              <tbody>
                {members.data.map((member) => {
                  const self = member.user_id === myId
                  const lastOwner = member.role === 'owner' && owners <= 1
                  return (
                    <tr key={member.user_id}>
                      <td>
                        {member.name ? <>{member.name} <span className="muted">{member.email}</span></> : member.email}
                        {self && <span className="muted small"> · you</span>}
                      </td>
                      <td>
                        {owner && !lastOwner ? (
                          <RolePicker
                            value={member.role}
                            disabled={setRole.isPending}
                            onChange={(next) => setRole.mutate({ userId: member.user_id, role: next })}
                          />
                        ) : (
                          <Badge>{ROLE_LABELS[member.role]}</Badge>
                        )}
                      </td>
                      <td className="muted">{timeAgo(member.created_at)}</td>
                      <td className="cell-actions">
                        {lastOwner ? (
                          <span className="muted small">The only owner</span>
                        ) : (
                          (owner || self) && (
                            <Button
                              variant="ghost"
                              busy={remove.isPending && remove.variables === member.user_id}
                              onClick={() => {
                                const question = self
                                  ? `Leave ${workspace.name}? You will lose access to its shows.`
                                  : `Take ${member.email} out of this workspace? Any API keys they made stop working.`
                                if (!window.confirm(question)) return
                                remove.mutate(member.user_id, { onSuccess: () => self && navigate('/studio') })
                              }}
                            >
                              {self ? 'Leave' : 'Remove'}
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {owner && invites.data && invites.data.length > 0 && (
          <div className="stack-sm">
            <h3 className="small muted">Waiting to be accepted</h3>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Invited</th><th>Role</th><th>Sent</th><th>Expires</th><th /></tr></thead>
                <tbody>
                  {invites.data.map((pending) => (
                    <tr key={pending.id}>
                      <td>{pending.email}</td>
                      <td><Badge>{ROLE_LABELS[pending.role]}</Badge></td>
                      <td className="muted">{timeAgo(pending.created_at)}</td>
                      <td className="muted">{timeUntil(pending.expires_at)}</td>
                      <td className="cell-actions">
                        <Button
                          variant="ghost"
                          busy={cancel.isPending && cancel.variables === pending.id}
                          onClick={() => {
                            if (window.confirm(`Cancel the invitation to ${pending.email}? Their link stops working.`)) cancel.mutate(pending.id)
                          }}
                        >
                          Cancel
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {owner && members.data?.length === 1 && invites.data?.length === 0 && (
          <p className="muted small">
            It is just you here. Invite someone to share the work: an editor can upload and publish, a viewer can only read.
            {workspace.plan !== 'premium' && ' Working with other people needs Premium.'}
          </p>
        )}
      </div>
    </Card>
  )
}

function ApiKeysCard({ workspace }: { workspace: Workspace }) {
  const keys = useApiKeys(workspace.id)
  const create = useCreateApiKey(workspace.id)
  const revoke = useRevokeApiKey(workspace.id)
  const [name, setName] = useState('')
  const [scope, setScope] = useState<KeyScope>('write')
  const [secret, setSecret] = useState<string | null>(null)
  const owner = workspace.role === 'owner'
  const readExample = `curl --fail-with-body "${window.location.origin}/api/v1/workspaces/${workspace.id}/shows" \\
  -H "Authorization: Bearer $INDIEFUTURE_API_KEY"`

  return (
    <Card title="API keys" actions={<a href="/docs" target="_blank" rel="noopener noreferrer">API documentation</a>}>
      <div className="stack">
        <p className="muted">Connect your server, scripts or agents to <strong>{workspace.name}</strong>. Each key is limited to this workspace and its shows and episodes, never your other workspaces.</p>
        <p className="muted small">Read-only keys can fetch content. Read-and-write keys can also create and edit shows and episodes, delete episodes and request processing. They cannot delete a show, manage members, create keys or change billing. Keep keys on your server, not in browser code.</p>
        <CopyField label="Workspace ID" value={workspace.id} />
        {!owner && <p className="muted">Only a workspace owner can create or revoke API keys.</p>}
        {secret && <SecretNotice secret={secret} message="Copy this key now. It will not be shown again." onDone={() => setSecret(null)} />}
        {owner && (
          <form
            className="row align-end"
            onSubmit={(event) => {
              event.preventDefault()
              create.mutate(
                { name: name.trim(), scope },
                {
                  onSuccess: (created) => {
                    setSecret(created.secret)
                    setName('')
                  },
                },
              )
            }}
          >
            <Field label="Key name">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="My website" maxLength={80} required />
            </Field>
            <Field label="Access">
              <select value={scope} onChange={(e) => setScope(e.target.value as KeyScope)}>
                <option value="write">Read and write</option>
                <option value="read">Read only</option>
              </select>
            </Field>
            <Button type="submit" busy={create.isPending} disabled={!name.trim() || secret !== null}>Create key</Button>
          </form>
        )}
        {create.error && <ErrorNote error={create.error} />}
        {revoke.error && <ErrorNote error={revoke.error} />}
        {keys.isPending && <LoadingBlock />}
        {keys.isError && <ErrorNote error={keys.error} />}
        {keys.data?.length === 0 && <EmptyState title="No API keys" />}
        {keys.data && keys.data.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Name</th><th>Key</th><th>Access</th><th>Created</th><th>Last used</th><th /></tr></thead>
              <tbody>
                {keys.data.map((key) => (
                  <tr key={key.id} className={key.revoked_at ? 'is-muted' : undefined}>
                    <td>{key.name}</td>
                    <td><code>{key.prefix}…</code></td>
                    <td>{key.scope === 'write' ? 'Read and write' : 'Read only'}</td>
                    <td className="muted">{timeAgo(key.created_at)}</td>
                    <td className="muted">{timeAgo(key.last_used_at)}</td>
                    <td className="cell-actions">
                      {key.revoked_at ? (
                        <Badge>Revoked</Badge>
                      ) : (
                        owner && (
                          <Button variant="ghost" busy={revoke.isPending && revoke.variables === key.id} onClick={() => revoke.mutate(key.id)}>
                            Revoke
                          </Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <details>
          <summary>Make your first API request</summary>
          <div className="stack">
            <p>Set <code>INDIEFUTURE_API_KEY</code> in your server or shell environment to the secret you copied, then list this workspace’s shows:</p>
            <CodeBlock value={readExample} />
            <CopyButton text={readExample} label="Copy request" />
            <p className="muted small">Use a read-and-write key for mutations. Revoking a key immediately blocks new requests. Paid processing still requires an explicit request and sufficient credits.</p>
            <div className="row">
              <a href="/docs" target="_blank" rel="noopener noreferrer">Read/write API reference</a>
              <a href="/api/v1/openapi.json" target="_blank" rel="noopener noreferrer">OpenAPI JSON</a>
            </div>
          </div>
        </details>
      </div>
    </Card>
  )
}
